import { UTCDate } from '@date-fns/utc';
import { BaaSProviders } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { format, isFuture, isValid } from 'date-fns';
import { round } from 'lodash';
import { InvoiceEvidenceView } from '../../types/entity-types';
import { DataEntities } from './data-provider';
import { FALLBACK_FX_RATE_DATE } from './fallback-fx-data';
import { FxData } from './use-fx';

const DATE_FORMAT = 'yyyy-MM-dd';
const TODAY = format(new UTCDate(), DATE_FORMAT);

export const getLinkedData = (data: Partial<DataEntities>, fxData: FxData) => {
  // Add Sentry breadcrumb at the start of data linking
  Sentry.addBreadcrumb({
    category: 'data_linking',
    message: 'Starting data linking process',
    level: 'info',
    data: {
      entityCounts: {
        organisations: data.organisations?.length || 0,
        companies: data.companies?.length || 0,
        accounts: data.accounts?.length || 0,
        transactions: data.transactions?.length || 0,
        vendors: data.vendors?.length || 0,
        categories: data.categories?.length || 0,
        evidence: data.evidence?.length || 0,
        beneficiaries: data.beneficiaries?.length || 0,
        employees: data.employees?.length || 0,
        payrolls: data.payrolls?.length || 0,
        payrollRecords: data.payrollRecords?.length || 0,
      },
      fxData: {
        base: fxData.base,
        ratesCount: Object.keys(fxData.rates || {}).length,
      },
    },
  });

  // We should not initiate the data linking if entity data is missing
  if (!validateRequiredData(data)) {
    Sentry.addBreadcrumb({
      data: {
        data,
        message: 'Missing entity data',
      },
    });

    throw new Error('Missing entity data');
  }

  // Entity linking layer
  getLinkedOrganisations(data);
  getLinkedCompanies(data);
  getLinkedAccounts(data, fxData);
  getLinkedTransactions(data, fxData);
  getLinkedVendors(data);
  getLinkedCategories(data);
  getLinkedEvidence(data, fxData);
  getLinkedBeneficiaries(data);
  getLinkedDocuments(data);
  getLinkedEmployees(data);
  getLinkedPayrolls(data);
  getLinkedPayrollRecords(data);

  // At this point data entities have been interlinked
  // Each entity points to the respective linked entity object
  // data.organisations.transactions[0] === data.transactions[0] // true
  return data;
};

// Links organisations to the necessary entities
const getLinkedOrganisations = (data: DataEntities) => {
  const { organisations, companies, transactions, accounts, vendors, categories, evidence, documents } = data;

  organisations.forEach((o) => {
    const companyList = companies.filter((c) => c.organisationId === o.id);

    // An organisation should always have at least one company
    if (!companyList.length) {
      Sentry.addBreadcrumb({
        data: {
          organisationId: o.id,
          companyIdList: companyList.map((c) => c.id),
          message: 'No company linked to organisation in getLinkedOrganisations',
        },
      });

      throw new Error(`No company linked to organisation ${o.id} found`);
    }

    o.companies = companyList;
    o.accounts = accounts.filter((a) => a.organisationId === o.id);
    o.transactions = transactions.filter((t) => t.organisationId === o.id);
    o.vendors = vendors.filter((v) => v.organisationId === o.id);
    o.categories = categories.filter((c) => c.organisationId === o.id);
    o.evidence = evidence.filter((e) => e.organisationId === o.id);
    o.documents = documents.filter((d) => d.organisationId === o.id);
  });

  return organisations;
};

// Links companies to the necessary entities
const getLinkedCompanies = (data: DataEntities) => {
  const { organisations, companies, accounts } = data;

  companies.forEach((c) => {
    const organisation = organisations.find((o) => o.id === c.organisationId);

    // A company should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          companyId: c.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to company not found in getLinkedCompanies',
        },
      });

      throw new Error(`Organisation linked to company ${c.id} not found`);
    }

    c.organisation = organisation;
    // company.accounts[] is required so we should ideally throw an error if no accounts belonging to the company are found
    // However, this may be an inconsistency in the BE models because when a company signs up they don't have any accounts
    // Context: https://seapointfinance.slack.com/archives/C07S0H7F2Q5/p1734706656939379?thread_ts=1734703709.416449&cid=C07S0H7F2Q5
    // TODO: review BE data models inconsistencies
    c.accounts = accounts.filter((a) => a.companyId === c.id);
  });

  return companies;
};

// Links accounts to the necessary entities
const getLinkedAccounts = (data: DataEntities, fxData: FxData) => {
  const { organisations, accounts, companies, transactions } = data;

  accounts.forEach((a) => {
    const organisation = organisations.find((o) => o.id === a.organisationId);
    const company = companies.find((c) => c.id === a.companyId);

    // An account should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          accountId: a.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to account not found in getLinkedAccounts',
        },
      });

      throw new Error(`Organisation linked to account ${a.id} not found`);
    }

    // An account should always have a company
    if (!company) {
      Sentry.addBreadcrumb({
        data: {
          message: `Company linked to account ${a.id} not found in getLinkedAccounts`,
          accountId: a.id,
          companyIdList: companies.map((c) => c.id),
        },
      });

      throw new Error(`Company linked to account ${a.id} not found`);
    }

    a.organisation = organisation;
    a.company = company;
    // A linked account may not have transactions when linked so we should not throw an error if transactions are not found?
    // TODO: is it a BE data model inconsistency (account.transactions[] should be optional)?
    a.transactions = transactions.filter((t) => t.accountId === a.id);
    // TODO: remove them once the BE has started computing them (TENG-399)
    // Uses || which falls through on any falsy value including 0 (unlike ?? which only falls through on null or undefined values)
    a.balance = a.closingBookedBalance || a.interimAvailableBalance || a.expectedBalance || 0;
    a.available = a.interimAvailableBalance || a.expectedBalance || 0;

    // There are no registered institutions in BE for Modulr, thats why we are injecting only name and logo for them in the data provider layer
    if (!a.institution && a.fspAdapter && Object.values(BaaSProviders).map(String).includes(a.fspAdapter)) {
      a.institution = {
        name: 'Seapoint',
        logo: '/logo-seapoint-round.svg',
      };
    }

    const accountCurrencyFxRate = fxData.rates[TODAY]?.[a.currency] ?? fxData.rates[FALLBACK_FX_RATE_DATE][a.currency];

    // Example:
    // Account currency is EUR
    // Base currency is GBP
    // FX rate GBP:EUR is 1.18 (1 GBP = 1.18 EUR)
    // baseCurrency is GBP
    // baseCurrencyFXRate is 1.18
    // baseCurrencyBalance is 100 EUR / 1.18 = 84.75 GBP
    a.baseCurrency = fxData.base;
    a.baseCurrencyFXRate = accountCurrencyFxRate;
    a.baseCurrencyBalance = round(a.balance / accountCurrencyFxRate, 2);
    a.baseCurrencyAvailable = round(a.available / accountCurrencyFxRate, 2);
  });

  return accounts;
};

// Links transactions to the necessary entities
const getLinkedTransactions = (data: DataEntities, fxData: FxData) => {
  const { organisations, transactions, accounts, vendors, evidence, beneficiaries } = data;

  transactions.forEach((t) => {
    const organisation = organisations.find((o) => o.id === t.organisationId);
    const account = accounts.find((a) => a.id === t.accountId);

    // A transaction should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          transactionId: t.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to transaction not found in getLinkedTransactions',
        },
      });

      throw new Error(`Organisation linked to transaction ${t.id} not found`);
    }

    // A transaction should always have an account
    if (!account) {
      Sentry.addBreadcrumb({
        data: {
          transactionId: t.id,
          accountIdList: accounts.map((a) => a.id),
          message: 'Account linked to transaction not found in getLinkedTransactions',
        },
      });

      throw new Error(`Account linked to transaction ${t.id} not found`);
    }

    t.organisation = organisation;
    t.account = account;
    t.vendor = vendors.find((v) => v.id === t.vendorId);
    t.evidence = evidence.filter((e) => e.transactionId === t.id);
    t.beneficiary = beneficiaries.find((b) => b.id === t.beneficiaryId);

    const transactionDate = format(t.fspDate, DATE_FORMAT);
    // A transaction fspDate can be in the future if it was initiated on a weekend day
    // We don't have fx data for future dates so we use today's fx rate to avoid a runtime error
    const isTransactionDateInTheFuture = isFuture(transactionDate);

    const rateDate = isTransactionDateInTheFuture ? TODAY : transactionDate;
    const txnDateFxRate = fxData.rates[rateDate]?.[t.currency] ?? fxData.rates[FALLBACK_FX_RATE_DATE][t.currency];

    // Example:
    // Txn amount is 100
    // Txn currency is EUR
    // User selected to display UX amounts in GBP
    // FX rate GBP:EUR is 1.18
    // baseCurrency is GBP
    // baseCurrencyFXRate is 1.18
    // baseCurrencyAmount is 100 EUR / 1.18 = 84.75 GBP
    t.baseCurrency = fxData.base;
    t.baseCurrencyFXRate = txnDateFxRate;
    // Rounding numbers in JS is a surprisingly complex topic. That is why I'd better leave it to professionals: lodash
    // https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary#comment137379887_11832950
    t.baseCurrencyAmount = round(t.amount / txnDateFxRate, 2);
  });

  return transactions;
};

// Links vendors to the necessary entities
const getLinkedVendors = (data: DataEntities) => {
  const { organisations, vendors, categories, transactions } = data;

  vendors.forEach((v) => {
    const organisation = organisations.find((o) => o.id === v.organisationId);

    // A vendor should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          vendorId: v.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to vendor not found in getLinkedVendors',
        },
      });

      throw new Error(`Organisation linked to vendor ${v.id} not found`);
    }

    v.organisation = organisation;
    v.transactions = transactions.filter((t) => t.vendorId === v.id);
    v.category = categories.find((c) => c.id === v.categoryId);
  });

  return vendors;
};

// Links categories to the necessary entities
const getLinkedCategories = (data: DataEntities) => {
  const { organisations, categories, vendors } = data;

  categories.forEach((c) => {
    const organisation = organisations.find((o) => o.id === c.organisationId);

    // A category should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          categoryId: c.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to category not found in getLinkedCategories',
        },
      });

      throw new Error(`Organisation linked to category ${c.id} not found`);
    }

    c.organisation = organisation;
    c.vendors = vendors.filter((v) => v.categoryId === c.id);
  });

  return categories;
};

// Links evidence to the necessary entities
const getLinkedEvidence = (data: DataEntities, fxData: FxData) => {
  const { organisations, evidence, transactions, documents, beneficiaries } = data;

  evidence.forEach((e) => {
    const organisation = organisations.find((o) => o.id === e.organisationId);

    // An evidence should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          evidenceId: e.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to evidence not found in getLinkedEvidence',
        },
      });

      throw new Error(`Organisation linked to evidence ${e.id} not found`);
    }

    e.organisation = organisation;
    e.transaction = transactions.find((t) => t.id === e.transactionId);
    e.document = documents.find((d) => d.id === e.documentId);

    if (e.suggestedBeneficiaryId) {
      e.suggestedBeneficiary = beneficiaries.find((b) => b.id === e.suggestedBeneficiaryId);
    }

    if (!e.invoiceDetails?.currency || !e.invoiceDetails?.totalAmount) {
      return;
    }

    // e.billingDates.invoiceDate is AI generated so we need to validate it
    const isInvoiceDateValid = isValidDate(e);

    const evidenceDate = format(isInvoiceDateValid ? e.billingDates.invoiceDate : TODAY, DATE_FORMAT);
    // An invoiceDate should never be in the future. However, we need to plan for this edge case.
    // We don't have fx data for future dates so we use today's fx rate to avoid a runtime error
    const isEvidenceDateInTheFuture = isFuture(evidenceDate);

    const rateDate = isEvidenceDateInTheFuture ? TODAY : evidenceDate;
    const evidenceDateFxRate =
      fxData.rates[rateDate]?.[e.invoiceDetails.currency] ??
      fxData.rates[FALLBACK_FX_RATE_DATE][e.invoiceDetails.currency];

    // Example:
    // Invoice amount is 100
    // Invoice currency is EUR
    // User selected to display UX amounts in GBP
    // FX rate GBP:EUR is 1.18
    // baseCurrency is GBP
    // baseCurrencyFXRate is 1.18
    // baseCurrencyAmount is 100 EUR / 1.18 = 84.75 GBP
    e.baseCurrency = fxData.base;
    e.baseCurrencyFXRate = evidenceDateFxRate;
    // Rounding numbers in JS is a surprisingly complex topic. That is why I'd better leave it to professionals: lodash
    // https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary#comment137379887_11832950
    e.baseCurrencyAmount = round(e.invoiceDetails.totalAmount / evidenceDateFxRate, 2);
  });

  return evidence;
};

// Links documents to the necessary entities
const getLinkedDocuments = (data: DataEntities) => {
  const { documents, organisations, evidence } = data;

  documents.forEach((d) => {
    const organisation = organisations.find((o) => o.id === d.organisationId);

    // A document should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          document: d,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to document not found in getLinkedDocuments',
        },
      });

      throw new Error(`Organisation linked to document ${d.id} not found`);
    }

    d.organisation = organisation;
    d.evidence = evidence.find((e) => e.documentId === d.id);
  });

  return documents;
};

// Links beneficiaries to the necessary entities
const getLinkedBeneficiaries = (data: DataEntities) => {
  const { organisations, companies, transactions, beneficiaries } = data;

  beneficiaries.forEach((b) => {
    const organisation = organisations.find((o) => o.id === b.organisationId);
    const company = companies.find((c) => c.id === b.companyId);

    // A beneficiary should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          beneficiaryId: b.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to beneficiary not found in getLinkedBeneficiaries',
        },
      });

      throw new Error(`Organisation linked to beneficiary ${b.id} not found`);
    }

    // A beneficiary should always have a company
    if (!company) {
      Sentry.addBreadcrumb({
        data: {
          beneficiaryId: b.id,
          companyIdList: companies.map((c) => c.id),
          message: 'Company linked to beneficiary not found in getLinkedBeneficiaries',
        },
      });

      throw new Error(`Company linked to beneficiary ${b.id} not found`);
    }

    b.organisation = organisation;
    b.company = company;
    b.transactions = transactions.filter((t) => t.beneficiaryId === b.id);
  });

  return beneficiaries;
};

// Links employees to the necessary entities
const getLinkedEmployees = (data: DataEntities) => {
  const { organisations, companies, employees } = data;

  employees.forEach((e) => {
    const organisation = organisations.find((o) => o.id === e.organisationId);
    const company = companies.find((c) => c.id === e.companyId);

    // An employee should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          employeeId: e.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to employee not found in getLinkedEmployees',
        },
      });

      throw new Error(`Organisation linked to employee ${e.id} not found`);
    }

    // An employee should always have a company
    if (!company) {
      Sentry.addBreadcrumb({
        data: {
          employeeId: e.id,
          companyIdList: companies.map((c) => c.id),
          message: 'Company linked to employee not found in getLinkedEmployees',
        },
      });

      throw new Error(`Company linked to employee ${e.id} not found`);
    }

    e.organisation = organisation;
    e.company = company;
  });

  return employees;
};

// Links payrolls to the necessary entities
const getLinkedPayrolls = (data: DataEntities) => {
  const { payrolls, organisations, companies, payrollRecords } = data;

  payrolls.forEach((p) => {
    const organisation = organisations.find((o) => o.id === p.organisationId);
    const company = companies.find((c) => c.id === p.companyId);
    const records = payrollRecords.filter((r) => r.payrollId === p.id);

    // A payroll should always have an organisation
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          payrollId: p.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to payroll not found in getLinkedPayrolls',
        },
      });

      throw new Error(`Organisation linked to payroll ${p.id} not found`);
    }

    // A payroll should always have a company
    if (!company) {
      Sentry.addBreadcrumb({
        data: {
          payrollId: p.id,
          companyIdList: companies.map((c) => c.id),
          message: 'Company linked to payroll not found in getLinkedPayrolls',
        },
      });

      throw new Error(`Company linked to payroll ${p.id} not found`);
    }

    p.organisation = organisation;
    p.company = company;
    p.records = records;
  });

  return payrolls;
};

const getLinkedPayrollRecords = (data: DataEntities) => {
  const { payrollRecords, employees, payrolls, organisations } = data;
  payrollRecords.forEach((r) => {
    const organisation = organisations.find((o) => o.id === r.organisationId);
    if (!organisation) {
      Sentry.addBreadcrumb({
        data: {
          payrollRecordId: r.id,
          organisationIdList: organisations.map((o) => o.id),
          message: 'Organisation linked to payroll record not found in getLinkedPayrollRecords',
        },
      });

      throw new Error(`Organisation linked to payroll record ${r.id} not found`);
    }

    const employee = employees.find((e) => e.id === r.employeeId);
    if (!employee) {
      Sentry.addBreadcrumb({
        data: {
          payrollRecordId: r.id,
          employeeIdList: employees.map((e) => e.id),
          message: 'Employee linked to payroll record not found in getLinkedPayrollRecords',
        },
      });

      throw new Error(`Employee linked to payroll record ${r.id} not found`);
    }
    const payroll = payrolls.find((p) => p.id === r.payrollId);
    if (!payroll) {
      Sentry.addBreadcrumb({
        data: {
          payrollRecordId: r.id,
          payrollIdList: payrolls.map((p) => p.id),
          message: 'Payroll linked to payroll record not found in getLinkedPayrollRecords',
        },
      });

      throw new Error(`Payroll linked to payroll record ${r.id} not found`);
    }

    r.employee = employee;
    r.payroll = payroll;
    r.organisation = organisation;
  });

  return payrollRecords;
};

/**
 * Type guard that validates if a partial DataEntities object contains all required entity arrays.
 *
 * @param {Partial<DataEntities>} data - The data object to validate
 * @returns {boolean} - Type predicate indicating if the object is a complete DataEntities
 *
 * @example
 * const partialData: Partial<DataEntities> = {
 *   organisations: [],
 *   companies: []
 *   // missing other required arrays
 * };
 *
 * if (validateRequiredData(partialData)) {
 *   // TypeScript now knows partialData has all required arrays
 *   partialData.transactions // typed as TransactionView[]
 * }
 */
const validateRequiredData = (data: Partial<DataEntities>): data is DataEntities => {
  const requiredEntities = [
    'organisations',
    'companies',
    'accounts',
    'transactions',
    'vendors',
    'categories',
    'evidence',
    'employees',
    'payrolls',
  ];
  const missingEntities = [];
  const emptyEntities = [];

  for (const entityName of requiredEntities) {
    const entityData = data[entityName as keyof DataEntities];
    if (!entityData) {
      missingEntities.push(entityName);
    } else if (Array.isArray(entityData) && entityData.length === 0) {
      emptyEntities.push(entityName);
    }
  }

  const isValid = !!(
    data.organisations &&
    data.companies &&
    data.accounts &&
    data.transactions &&
    data.vendors &&
    data.categories &&
    data.evidence &&
    data.employees &&
    data.payrolls
  );

  // Add detailed Sentry breadcrumb for validation results
  if (!isValid || missingEntities.length > 0 || emptyEntities.length > 0) {
    Sentry.addBreadcrumb({
      category: 'data_validation',
      message: 'Entity data validation details',
      level: 'warning',
      data: {
        isValid,
        missingEntities,
        emptyEntities,
        entityCounts: {
          organisations: data.organisations?.length || 0,
          companies: data.companies?.length || 0,
          accounts: data.accounts?.length || 0,
          transactions: data.transactions?.length || 0,
          vendors: data.vendors?.length || 0,
          categories: data.categories?.length || 0,
          evidence: data.evidence?.length || 0,
          beneficiaries: data.beneficiaries?.length || 0,
          employees: data.employees?.length || 0,
          payrolls: data.payrolls?.length || 0,
        },
      },
    });
  }

  return isValid;
};

export const isValidDate = (
  evidence: InvoiceEvidenceView,
): evidence is InvoiceEvidenceView & { billingDates: { invoiceDate: string } } => {
  return isValid(new UTCDate(evidence.billingDates?.invoiceDate ?? ''));
};
